import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ClientToolsRegistry,
  TellentAssistantActionsService,
  TellentAssistantConfigService,
  TellentAssistantService,
  TellentAssistantThreadsService,
  ToolTitlesFacade,
} from '@core/tellent-assistant-logic';
import type {
  AskDecisionOption,
  ChatPart,
  ConversationStarter,
} from '@core/tellent-assistant-types';
import {
  TellentAssistantEmptyStateComponent,
  TellentAssistantHeaderComponent,
  TellentAssistantInputComponent,
  TellentAssistantMessageComponent,
  TellentAssistantThreadDrawerComponent,
  type ProposalDecisionEvent,
} from '@core/tellent-assistant-ui';
import { AppKind, ORG_GUID } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import {
  ConnectedOverlayDirective,
  FIXED_OVERLAY_LEVEL,
  SpinnerComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import { TellentAssistantScrollAnchorService } from './tellent-assistant-scroll-anchor.service';

export type TellentAssistantMode = 'compact' | 'fullscreen' | 'minimized';

type ProposeActionPart = Extract<ChatPart, { kind: 'propose-action' }>;

/** The proposal's args as the params record the execute endpoint expects. */
const argsRecord = (part: ProposeActionPart): Record<string, unknown> =>
  typeof part.args === 'object' && part.args !== null ? <Record<string, unknown>>part.args : {};

const without = (ids: ReadonlySet<string>, id: string): ReadonlySet<string> => {
  const next = new Set(ids);
  next.delete(id);
  return next;
};

@Component({
  selector: 'tellent-assistant-chat-panel',
  imports: [
    TellentAssistantHeaderComponent,
    TellentAssistantEmptyStateComponent,
    TellentAssistantInputComponent,
    TellentAssistantThreadDrawerComponent,
    TellentAssistantMessageComponent,
    ConnectedOverlayDirective,
    SpinnerComponent,
    TranslatePipe,
    CdkDrag,
    CdkDragHandle,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tellent-assistant-chat-panel.component.html',
  styleUrl: './tellent-assistant-chat-panel.component.less',
  providers: [TellentAssistantScrollAnchorService],
  host: {
    '[class.expanded]': 'expanded()',
    '[class.minimized]': 'minimized()',
    '[class.first-paint-done]': 'firstPaintDone()',
    '[class.embedded]': 'embedded()',
  },
})
export class TellentAssistantChatPanelComponent {
  private readonly i18n = inject(I18nService);
  private readonly windowRef = inject(WindowRef);
  private readonly orgGuid = inject(ORG_GUID, { optional: true });
  private readonly urlBuilder = injectTellentAppUrlBuilder();
  private readonly drawerOverlay = viewChild<ConnectedOverlayDirective>('drawerOverlay');
  private readonly bodyScroll = viewChild<ElementRef<HTMLDivElement>>('bodyScroll');
  private readonly scrollSpacer = viewChild<ElementRef<HTMLDivElement>>('scrollSpacer');
  private readonly inputRef = viewChild(TellentAssistantInputComponent);
  private readonly clientToolsRegistry = inject(ClientToolsRegistry);
  private readonly toolTitlesFacade = inject(ToolTitlesFacade);
  private readonly scrollAnchor = inject(TellentAssistantScrollAnchorService);
  private readonly actionsService = inject(TellentAssistantActionsService);

  protected readonly headerRef = viewChild(TellentAssistantHeaderComponent);
  protected readonly assistantService = inject(TellentAssistantService);
  protected readonly threadsService = inject(TellentAssistantThreadsService);
  protected readonly config = inject(TellentAssistantConfigService);
  protected readonly toolTitles = this.toolTitlesFacade.titles;
  protected readonly drawerOpen = signal<boolean>(false);
  protected readonly firstPaintDone = signal<boolean>(false);
  protected readonly defaultTitle = this.i18n.translate('tlc.tellent_assistant.chat_panel.title');
  protected readonly currentThread = computed(() => {
    const id = this.assistantService.currentThreadId();
    if (!id) return null;
    return this.threadsService.threads().find(t => t.id === id) ?? null;
  });
  protected readonly title = computed(() => this.currentThread()?.title || this.defaultTitle);
  protected readonly currentThreadPinned = computed(() => this.currentThread()?.pinned ?? false);
  protected readonly drawerPositions = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];
  protected readonly expanded = computed(() => this.mode() === 'fullscreen');
  protected readonly minimized = computed(() => this.mode() === 'minimized');
  protected readonly dragPosition = computed(() =>
    this.expanded() ? { x: 0, y: 0 } : { x: this.dockOffsetX(), y: 0 },
  );

  protected readonly scrollToBottomOnThreadOpen = effect(() => {
    const id = this.assistantService.currentThreadId();
    const msgs = this.assistantService.messages();
    const host = this.bodyScroll()?.nativeElement;
    if (!host || !id) return;
    if (id === this.lastScrolledThreadId) return;
    if (msgs.length === 0) return;
    this.lastScrolledThreadId = id;
    requestAnimationFrame(() => {
      host.scrollTop = host.scrollHeight;
    });
  });

  protected readonly anchorPromptOnSend = effect(() => {
    const threadId = this.assistantService.currentThreadId();
    const hasMessages = this.assistantService.messages().length > 0;
    const host = this.bodyScroll()?.nativeElement;
    const spacer = this.scrollSpacer()?.nativeElement;
    if (!host || !spacer) return;
    this.scrollAnchor.sync({ threadId, hasMessages, host, spacer });
  });

  protected readonly atBottom = signal<boolean>(true);

  protected readonly refreshFadeOnContent = effect(() => {
    this.assistantService.messages();
    this.assistantService.runStatus();
    const host = this.bodyScroll()?.nativeElement;
    if (!host) return;
    requestAnimationFrame(() => this.updateAtBottom(host));
  });

  protected readonly clearInputOnThreadSwitch = effect(() => {
    const id = this.assistantService.currentThreadId();
    const input = this.inputRef();
    if (this.previousThreadId === undefined) {
      this.previousThreadId = id;
      return;
    }
    if (id === this.previousThreadId) return;
    this.previousThreadId = id;
    input?.clear();
  });

  protected readonly loadActionResolutionsOnThread = effect(() => {
    const threadId = this.assistantService.currentThreadId();
    if (!threadId) return;
    // Only fetch when the thread actually contains proposals (most don't);
    // the service dedups per thread, so streaming updates don't re-fetch.
    // Load failure is cosmetic (cards render idle); backend idempotency
    // prevents a stale-looking card from double-executing.
    const hasProposals = this.assistantService
      .messages()
      .some(message => message.parts.some(part => part.kind === 'propose-action'));
    if (hasProposals) this.actionsService.loadForThread(threadId).catch(() => undefined);
  });

  // Decisions happen inline on each proposal card; these track per-proposal
  // dispatch state so the card can disable its buttons / show an error row.
  protected readonly busyProposals = signal<ReadonlySet<string>>(new Set());
  protected readonly failedProposals = signal<ReadonlySet<string>>(new Set());

  public readonly mode = input.required<TellentAssistantMode>();
  public readonly dockOffsetX = input<number>(0);
  public readonly embedded = input<boolean>(false);
  public readonly close = output<void>();
  public readonly expand = output<void>();
  public readonly collapse = output<void>();
  public readonly minimize = output<void>();
  public readonly restore = output<void>();
  public readonly dockOffsetXChange = output<number>();

  public readonly renderToolNames = this.clientToolsRegistry.ids;

  private lastScrolledThreadId: string | null = null;
  private previousThreadId: string | null | undefined = undefined;
  private dragged = false;

  constructor() {
    this.toolTitlesFacade.ensureLoaded();
    afterNextRender(() => this.firstPaintDone.set(true));
  }

  public onDragStarted(): void {
    this.dragged = true;
  }

  public onDragEnded(event: CdkDragEnd): void {
    if (this.expanded()) return;
    this.dockOffsetXChange.emit(event.source.getFreeDragPosition().x);
  }

  public onDragMoved(): void {
    this.drawerOverlay()?.overlayRef?.updatePosition();
  }

  public onHeaderClick(): void {
    if (this.dragged) {
      this.dragged = false;
      return;
    }
    if (this.minimized()) this.restore.emit();
  }

  public readonly isDeleting = (id: string): boolean => this.threadsService.isThreadDeleting(id);

  public readonly resolveRenderComponent = (
    toolName: string,
    _args: unknown,
    status: 'running' | 'done' | 'error',
  ) =>
    this.clientToolsRegistry.shouldRender(toolName, status)
      ? this.clientToolsRegistry.byId(toolName)?.component
      : undefined;

  public readonly actionResolver = (proposalId: string) =>
    this.actionsService.resolutionOf(proposalId);

  public readonly resultLinkResolver = (proposalId: string) =>
    this.actionsService.resultLinkOf(proposalId);

  public readonly decisionBusyResolver = (proposalId: string): boolean =>
    this.busyProposals().has(proposalId);

  public readonly decisionErrorResolver = (proposalId: string): boolean =>
    this.failedProposals().has(proposalId);

  public deleteThread(id: string): void {
    this.threadsService.deleteThread(id);
  }

  protected togglePin(): void {
    const thread = this.currentThread();
    if (!thread) return;
    this.threadsService.setThreadPinned(thread.id, !thread.pinned);
  }

  protected onDrawerAttach(): void {
    const host = this.drawerOverlay()?.overlayRef?.hostElement;
    if (host) host.style.zIndex = `${FIXED_OVERLAY_LEVEL + 1}`;
  }

  protected send(text: string, metadata?: Record<string, unknown>): void {
    this.scrollAnchor.arm();
    this.assistantService.send(text, metadata);
  }

  protected onDecisionAnswered(option: AskDecisionOption): void {
    if (option.label !== option.value) {
      this.send(option.value, { displayLabel: option.label });
    } else {
      this.send(option.value);
    }
  }

  protected skipDecision(): void {
    this.send(this.i18n.translate('tlc.tellent_assistant.proposal.skip'));
  }

  protected hasLaterUserTurn(index: number): boolean {
    return this.assistantService
      .messages()
      .some((message, i) => i > index && message.role === 'user');
  }

  protected async onProposalDecided({ part, decision }: ProposalDecisionEvent): Promise<void> {
    const threadId = this.assistantService.currentThreadId();
    if (!threadId) return;

    if (decision === 'skip') {
      this.actionsService.markCancelled(threadId, part.proposalId, part.action);
      return;
    }

    this.busyProposals.update(ids => new Set(ids).add(part.proposalId));
    this.failedProposals.update(ids => without(ids, part.proposalId));
    try {
      await this.actionsService.execute(threadId, part.proposalId, part.action, argsRecord(part));
    } catch {
      this.failedProposals.update(ids => new Set(ids).add(part.proposalId));
    } finally {
      this.busyProposals.update(ids => without(ids, part.proposalId));
    }
  }

  protected sendStarter(starter: ConversationStarter): void {
    this.scrollAnchor.arm();
    this.assistantService.sendStarter(starter);
  }

  protected newThread(): void {
    this.assistantService.newThread();
  }

  protected selectThread(id: string): void {
    this.assistantService.switchThread(id);
  }

  protected openHelpCenter(): void {
    const url = this.i18n.translate('app.support_fab.help_center_settings_url');
    this.windowRef.nativeWindow.open(url, '_blank');
  }

  protected openIntelligenceSettings(): void {
    if (!this.orgGuid) return;
    const url = this.urlBuilder(AppKind.TellentAdmin, { id: 'intelligence' }, this.orgGuid);
    this.windowRef.nativeWindow.location = url;
  }

  protected onBodyScroll(): void {
    const host = this.bodyScroll()?.nativeElement;
    if (host) this.updateAtBottom(host);
  }

  private updateAtBottom(host: HTMLElement): void {
    this.atBottom.set(host.scrollHeight - host.scrollTop - host.clientHeight <= 1);
  }
}
